import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["scanInput", "jobDisplay", "noJobMessage"]
  static values = {
    printers: Array
  }

  connect() {
    // Auto-focus the scan input when page loads
    this.scanInputTarget.focus()
    // Track focused item IDs for printer barcode assignment
    this.focusedItemIds = []
  }

  scan(event) {
    event.preventDefault()

    const scanInput = this.scanInputTarget.value.trim()

    if (!scanInput) {
      return
    }

    // Clear the input for next scan
    const inputValue = scanInput
    this.scanInputTarget.value = ''

    // Make AJAX request to scan endpoint
    fetch('/printer_station/scan', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ scan_input: inputValue })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        this.showError(data.error)
      } else {
        // Handle different scan types
        if (data.scan_type === 'printer') {
          this.handlePrinterScan(data)
        } else if (data.scan_type === 'item') {
          this.displayFocusedItems(data)
        } else {
          // Order scan - full display
          this.displayJob(data.job)
        }
      }
      // Re-focus input for next scan
      this.scanInputTarget.focus()
    })
    .catch(error => {
      console.error('Error:', error)
      this.showError('An error occurred while scanning')
      this.scanInputTarget.focus()
    })
  }

  handlePrinterScan(data) {
    if (this.focusedItemIds.length === 0) {
      this.showError('No focused item. Scan an item barcode first.')
      return
    }

    const printer = data.printer

    // Assign printer to all focused items
    const assignPromises = this.focusedItemIds.map(itemId => {
      return fetch(`/printer_station/assign_printer/${itemId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
        },
        body: JSON.stringify({ printer_id: printer.id })
      }).then(response => response.json())
    })

    Promise.all(assignPromises)
      .then(results => {
        const allSuccess = results.every(result => result.success)

        if (allSuccess) {
          // Update displayed items to show assigned printer
          this.focusedItemIds.forEach((itemId, index) => {
            const result = results[index]
            const row = this.jobDisplayTarget.querySelector(`[data-item-id="${itemId}"]`)
            if (row) {
              const statusCell = row.closest('tr').querySelector('.item-status')
              const printerCell = row.closest('tr').querySelector('.item-printer')
              if (statusCell) statusCell.innerHTML = this.statusBadgeHTML(result.item.status)
              if (printerCell) printerCell.textContent = result.item.printer_name
            }
          })

          this.showSuccess(`✓ Assigned to ${printer.name}`)
        } else {
          // Collect error messages from failed assignments
          const errors = results
            .filter(result => !result.success)
            .map(result => result.error || 'Unknown error')

          console.error('Assignment failures:', results)

          // Show detailed error message
          const errorMessage = errors.length === 1
            ? errors[0]
            : `${errors.length} assignments failed:\n${errors.join('\n')}`

          this.showError(errorMessage)
        }
      })
      .catch(error => {
        console.error('Error:', error)
        this.showError('An error occurred while assigning printer')
      })
  }

  displayFocusedItems(data) {
    this.noJobMessageTarget.classList.add('hidden')
    this.jobDisplayTarget.classList.remove('hidden')

    // Store focused item IDs for printer barcode assignment
    this.focusedItemIds = data.focused_items.map(item => item.id)

    const isMultiLocation = data.focused_items.length > 1

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-2xl font-semibold text-gray-900">Order #${data.job.ordernumber}</h3>
          <p class="mt-2 text-sm text-gray-500">Focused Item${isMultiLocation ? 's' : ''} - Ready for printer assignment</p>
          <div class="mt-4 bg-indigo-50 border border-indigo-200 rounded-md p-4">
            <p class="text-indigo-800 font-medium text-center text-lg">
              📷 Scan printer barcode to assign
            </p>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 200px;">Preview</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Item ID</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Printer</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${this.renderFocusedItems(data.focused_items, isMultiLocation)}
          </tbody>
        </table>
      </div>
    `

    this.jobDisplayTarget.innerHTML = html
  }

  renderFocusedItems(items, isMultiLocation) {
    let html = ''
    items.forEach((item, index) => {
      const isFirst = index === 0
      const borderClass = isMultiLocation ? 'border-l-4 border-l-green-500' : ''
      const bgClass = isMultiLocation ? 'bg-green-50' : 'bg-white'
      const itemIdDisplay = isFirst ? (item.order_item_id || '-') : ''
      const groupIndicator = isMultiLocation && isFirst ?
        '<span class="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 ml-2">Multi-location group</span>' : ''

      const thumbnailHTML = item.thumbnail_url ?
        `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 200px; min-width: 200px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
        `<div style="width: 200px; min-width: 200px; height: 229px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-sm">No image</div>`

      html += `
        <tr class="${borderClass} ${bgClass}">
          <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" style="min-width: 200px;">
            ${thumbnailHTML}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
            ${itemIdDisplay}${groupIndicator}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.product || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 item-status">
            ${this.statusBadgeHTML(item.status)}
          </td>
          <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 item-printer" data-item-id="${item.id}">${item.printer_name || 'Not assigned'}</td>
        </tr>
      `
    })
    return html
  }

  showSuccess(message) {
    const successDiv = document.createElement('div')
    successDiv.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-50'
    successDiv.textContent = message
    document.body.appendChild(successDiv)

    setTimeout(() => {
      successDiv.remove()
    }, 3000)
  }

  assignPrinter(event) {
    event.preventDefault()

    const button = event.currentTarget
    const itemId = button.dataset.itemId
    const select = button.parentElement.querySelector('select')
    const printerId = select.value

    if (!printerId) {
      alert('Please select a printer')
      return
    }

    // Disable button during request
    button.disabled = true
    button.textContent = 'Assigning...'

    fetch(`/printer_station/assign_printer/${itemId}`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content
      },
      body: JSON.stringify({ printer_id: printerId })
    })
    .then(response => response.json())
    .then(data => {
      if (data.error) {
        alert(data.error)
        button.disabled = false
        button.textContent = 'Assign'
      } else {
        // Update the UI to show assigned printer and new status
        const row = button.closest('tr')
        row.querySelector('.item-status').innerHTML = this.statusBadgeHTML(data.item.status)
        row.querySelector('.item-printer').textContent = data.item.printer_name

        // Show warning if file movement failed
        if (data.warning) {
          alert(`⚠️ ${data.warning}`)
        } else if (data.message) {
          console.log(data.message)
        }

        // Update button to show success
        button.textContent = 'Assigned!'
        button.classList.remove('bg-indigo-600', 'hover:bg-indigo-500')
        button.classList.add('bg-green-600')

        // Disable button and select after assignment
        select.disabled = true

        setTimeout(() => {
          button.disabled = false
          button.textContent = 'Assign'
          button.classList.remove('bg-green-600')
          button.classList.add('bg-indigo-600', 'hover:bg-indigo-500')
        }, 2000)
      }
    })
    .catch(error => {
      console.error('Error:', error)
      alert('An error occurred while assigning printer')
      button.disabled = false
      button.textContent = 'Assign'
    })
  }

  displayJob(job) {
    this.noJobMessageTarget.classList.add('hidden')
    this.jobDisplayTarget.classList.remove('hidden')

    const html = `
      <div class="bg-white shadow sm:rounded-lg mb-6">
        <div class="px-4 py-5 sm:px-6">
          <h3 class="text-2xl font-semibold text-gray-900">Order #${job.ordernumber}</h3>
          <div class="mt-2 grid grid-cols-2 gap-4">
            <div>
              <p class="text-sm text-gray-500">Production Date</p>
              <p class="text-sm font-medium text-gray-900">${job.production_date || 'Not set'}</p>
            </div>
            <div>
              <p class="text-sm text-gray-500">Job Status</p>
              <p class="text-sm font-medium text-gray-900">${job.status}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="overflow-x-auto shadow ring-1 ring-black/5 sm:rounded-lg">
        <table class="min-w-full divide-y divide-gray-300">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6" style="min-width: 200px;">Preview</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Item ID</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Product</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Color</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Size</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Location</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Status</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Printer</th>
              <th scope="col" class="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">Assign</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 bg-white">
            ${this.renderGroupedItems(job.items)}
          </tbody>
        </table>
      </div>
    `

    this.jobDisplayTarget.innerHTML = html
  }

  renderGroupedItems(items) {
    // Group items by order_item_id
    const groups = {}
    items.forEach(item => {
      const key = item.order_item_id || `single_${item.id}`
      if (!groups[key]) {
        groups[key] = []
      }
      groups[key].push(item)
    })

    // Render items with visual grouping
    let html = ''
    Object.keys(groups).forEach(orderItemId => {
      const groupItems = groups[orderItemId]
      const isMultiLocation = groupItems.length > 1

      groupItems.forEach((item, index) => {
        const isFirst = index === 0
        const isLast = index === groupItems.length - 1
        html += this.itemRowHTML(item, isMultiLocation, isFirst, isLast, orderItemId)
      })
    })

    return html
  }

  itemRowHTML(item, isMultiLocation, isFirst, isLast, groupId) {
    const borderClass = isMultiLocation ? 'border-l-4 border-l-indigo-500' : ''
    const bgClass = isMultiLocation ? 'bg-indigo-50' : ''
    const itemIdDisplay = isFirst || !isMultiLocation ? (item.order_item_id || '-') : ''
    const groupIndicator = isMultiLocation && isFirst ?
      '<span class="inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-800 ml-2">Multi-location</span>' : ''

    const thumbnailHTML = item.thumbnail_url ?
      `<img src="${item.thumbnail_url}" alt="Print preview" style="width: 200px; min-width: 200px; height: auto;" class="object-contain rounded border border-gray-300 bg-gray-200">` :
      `<div style="width: 200px; min-width: 200px; height: 229px;" class="flex items-center justify-center bg-gray-100 rounded border border-gray-300 text-gray-400 text-sm">No image</div>`

    return `
      <tr class="${borderClass} ${bgClass}">
        <td class="py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6" style="min-width: 200px;">
          ${thumbnailHTML}
        </td>
        <td class="whitespace-nowrap px-3 py-4 text-sm font-medium text-gray-900">
          ${itemIdDisplay}${groupIndicator}
        </td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.product || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.color || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.size || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">${item.print_location || '-'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 item-status">
          ${this.statusBadgeHTML(item.status)}
        </td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500 item-printer">${item.printer_name || 'Not assigned'}</td>
        <td class="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
          <div class="flex items-center space-x-2">
            <select class="rounded-md border-0 py-1 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 focus:ring-2 focus:ring-inset focus:ring-indigo-600 text-sm">
              <option value="">Select printer...</option>
              ${this.printerOptionsHTML()}
            </select>
            <button
              data-item-id="${item.id}"
              data-action="click->printer-station#assignPrinter"
              class="rounded-md bg-indigo-600 px-2.5 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-500">
              Assign
            </button>
          </div>
        </td>
      </tr>
    `
  }

  printerOptionsHTML() {
    return this.printersValue.map(printer => `<option value="${printer.id}">${printer.name}</option>`).join('')
  }

  statusBadgeHTML(status) {
    const badgeClass = this.getStatusBadgeClass(status)
    return `<span class="inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${badgeClass}">${status}</span>`
  }

  getStatusBadgeClass(status) {
    if (status.includes('Pending')) return 'bg-yellow-50 text-yellow-700 ring-yellow-600/20'
    if (status.includes('Print') || status.includes('Queued')) return 'bg-blue-50 text-blue-700 ring-blue-600/20'
    if (status.includes('Complete') || status.includes('Pass')) return 'bg-green-50 text-green-700 ring-green-600/20'
    if (status.includes('Fail')) return 'bg-red-50 text-red-700 ring-red-600/20'
    return 'bg-gray-50 text-gray-700 ring-gray-600/20'
  }

  showError(message) {
    this.jobDisplayTarget.classList.add('hidden')
    this.noJobMessageTarget.classList.remove('hidden')
    this.noJobMessageTarget.innerHTML = `
      <div class="rounded-md bg-red-50 p-4">
        <div class="flex">
          <div class="ml-3">
            <h3 class="text-sm font-medium text-red-800">Error</h3>
            <div class="mt-2 text-sm text-red-700">
              <p>${message}</p>
            </div>
          </div>
        </div>
      </div>
    `
  }
}
